import { useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, Users, Shield, Award } from "lucide-react";
import { SEO } from "@/components/seo/SEO";

export default function Offerte() {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    voornaam: "",
    achternaam: "",
    email: "",
    telefoon: "",
    adres: "",
    postcode: "",
    plaats: "",
    woningtype: "",
    diensten: [] as string[],
    bericht: "",
    privacy: false
  });

  const handleDienstChange = (dienst: string, checked: boolean) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        diensten: [...prev.diensten, dienst]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        diensten: prev.diensten.filter(d => d !== dienst)
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.privacy) {
      toast({
        title: "Privacy akkoord vereist",
        description: "U moet akkoord gaan met de privacy voorwaarden om verder te gaan.",
        variant: "destructive"
      });
      return;
    }

    toast({
      title: "Offerte aanvraag verzonden!",
      description: "We nemen binnen 24 uur contact met u op voor uw persoonlijke offerte."
    });

    // Reset form
    setFormData({
      voornaam: "",
      achternaam: "",
      email: "",
      telefoon: "",
      adres: "",
      postcode: "",
      plaats: "",
      woningtype: "",
      diensten: [],
      bericht: "",
      privacy: false
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="py-20">
        <SEO
          title="Gratis offerte aanvragen | SOL-VI"
          description="Ontvang binnen 24 uur een persoonlijke offerte voor zonnepanelen, warmtepomp, thuisbatterij of zonneboiler."
          type="website"
        />
        <div className="container mx-auto px-4">
          {/* Header Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Vraag Uw Gratis Offerte Aan</h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Ontvang binnen 24 uur een persoonlijke offerte op maat, 
              volledig gratis en zonder verplichtingen.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-12 max-w-6xl mx-auto">
            {/* Form */}
            <div className="lg:col-span-2">
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="text-2xl">Offerte Aanvraag</CardTitle>
                  <CardDescription>
                    Vul het formulier in en ontvang uw persoonlijke offerte
                  </CardDescription>
                </CardHeader>
                
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {/* Persoonlijke gegevens */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold border-b pb-2">Persoonlijke Gegevens</h3>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="voornaam">Voornaam *</Label>
                          <Input
                            id="voornaam"
                            value={formData.voornaam}
                            onChange={(e) => setFormData(prev => ({ ...prev, voornaam: e.target.value }))}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="achternaam">Achternaam *</Label>
                          <Input
                            id="achternaam"
                            value={formData.achternaam}
                            onChange={(e) => setFormData(prev => ({ ...prev, achternaam: e.target.value }))}
                            required
                          />
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="email">E-mailadres *</Label>
                          <Input
                            id="email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="telefoon">Telefoonnummer *</Label>
                          <Input
                            id="telefoon"
                            type="tel"
                            value={formData.telefoon}
                            onChange={(e) => setFormData(prev => ({ ...prev, telefoon: e.target.value }))}
                            required
                          />
                        </div>
                      </div>
                    </div>

                    {/* Adresgegevens */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold border-b pb-2">Adresgegevens</h3>
                      
                      <div>
                        <Label htmlFor="adres">Straat en huisnummer *</Label>
                        <Input
                          id="adres"
                          value={formData.adres}
                          onChange={(e) => setFormData(prev => ({ ...prev, adres: e.target.value }))}
                          required
                        />
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="postcode">Postcode *</Label>
                          <Input
                            id="postcode"
                            value={formData.postcode}
                            onChange={(e) => setFormData(prev => ({ ...prev, postcode: e.target.value }))}
                            required
                          />
                        </div>
                        <div>
                          <Label htmlFor="plaats">Plaats *</Label>
                          <Input
                            id="plaats"
                            value={formData.plaats}
                            onChange={(e) => setFormData(prev => ({ ...prev, plaats: e.target.value }))}
                            required
                          />
                        </div>
                      </div>

                      <div>
                        <Label htmlFor="woningtype">Type woning</Label>
                        <Select onValueChange={(value) => setFormData(prev => ({ ...prev, woningtype: value }))}>
                          <SelectTrigger>
                            <SelectValue placeholder="Selecteer uw woningtype" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="vrijstaand">Vrijstaande woning</SelectItem>
                            <SelectItem value="twee-onder-een-kap">Twee-onder-een-kap</SelectItem>
                            <SelectItem value="rijtjeshuis">Rijtjeshuis</SelectItem>
                            <SelectItem value="appartement">Appartement</SelectItem>
                            <SelectItem value="bungalow">Bungalow</SelectItem>
                            <SelectItem value="anders">Anders</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Diensten */}
                    <div className="space-y-4">
                      <h3 className="text-lg font-semibold border-b pb-2">Interesse in Diensten</h3>
                      
                      <div className="grid md:grid-cols-2 gap-4">
                        {[
                          { id: "zonnepanelen", label: "Zonnepanelen" },
                          { id: "warmtepomp", label: "Warmtepomp" },
                          { id: "thuisbatterij", label: "Thuisbatterij" },
                          { id: "zonneboiler", label: "Zonneboiler" }
                        ].map((dienst) => (
                          <div key={dienst.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={dienst.id}
                              checked={formData.diensten.includes(dienst.id)}
                              onCheckedChange={(checked) => handleDienstChange(dienst.id, checked as boolean)}
                            />
                            <Label htmlFor={dienst.id}>{dienst.label}</Label>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Bericht */}
                    <div>
                      <Label htmlFor="bericht">Aanvullende informatie</Label>
                      <Textarea
                        id="bericht"
                        placeholder="Vertel ons meer over uw wensen en situatie..."
                        value={formData.bericht}
                        onChange={(e) => setFormData(prev => ({ ...prev, bericht: e.target.value }))}
                        rows={4}
                      />
                    </div>

                    {/* Privacy */}
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="privacy"
                        checked={formData.privacy}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, privacy: checked as boolean }))}
                      />
                      <Label htmlFor="privacy" className="text-sm leading-relaxed">
                        Ik ga akkoord met de verwerking van mijn gegevens voor het opstellen van een offerte 
                        en eventuele toekomstige communicatie van SOL-VI. *
                      </Label>
                    </div>

                    <Button type="button" size="lg" className="w-full" variant="hero" onClick={() => (window as any).renderOpenSolarWidget()}>
                      Verstuur Offerte Aanvraag
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>

            {/* Info Sidebar */}
            <div className="space-y-6">
              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-secondary" />
                    Wat krijgt u?
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 bg-secondary rounded-full mt-2"></div>
                      <span className="text-sm">Persoonlijke offerte binnen 24 uur</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 bg-secondary rounded-full mt-2"></div>
                      <span className="text-sm">Gratis technisch advies</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 bg-secondary rounded-full mt-2"></div>
                      <span className="text-sm">Berekening van uw besparingen</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 bg-secondary rounded-full mt-2"></div>
                      <span className="text-sm">Informatie over subsidies</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <div className="h-1.5 w-1.5 bg-secondary rounded-full mt-2"></div>
                      <span className="text-sm">Geen verplichtingen</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="shadow-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" />
                    Waarom SOL-VI?
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <Users className="h-8 w-8 text-secondary" />
                    <div>
                      <div className="font-semibold">500+ Klanten</div>
                      <div className="text-sm text-muted-foreground">Tevreden en energieneutraal</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Award className="h-8 w-8 text-primary" />
                    <div>
                      <div className="font-semibold">15+ Jaar Ervaring</div>
                      <div className="text-sm text-muted-foreground">Gecertificeerde installateurs</div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <Shield className="h-8 w-8 text-accent" />
                    <div>
                      <div className="font-semibold">5 Jaar Garantie</div>
                      <div className="text-sm text-muted-foreground">Op alle installaties</div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="bg-muted rounded-lg p-6 text-center">
                <h3 className="font-semibold mb-2">Direct Contact?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Liever persoonlijk contact? Bel ons direct!
                </p>
                <Button variant="outline" asChild className="w-full">
                  <a href="tel:0617105786">
                    06 17 10 57 86
                  </a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}